import { WORLD_MAX_COORDINATE } from "./world-generation.js";
/** Autonomous machine warfare is separate from player, NPC and Sentinel authority. */
export const COMBAT_THEATER = Object.freeze({ id: "combat-theater-alpha", x: -115, z: 90,
    minX: -175, maxX: -65, minZ: 60, maxZ: 140 });
export const COMBAT_INTEREST_RADIUS = 220;
export const COMBAT_LIMITS = Object.freeze({ actors: 24, rockets: 32, eventsPerPacket: 96, recentEvents: 256,
    eventLifetimeTicks: 20, hounds: 12, bastions: 3, technicals: 2, maxTechnicals: 4, quadRounds: 16, quadDurationMs: 1200, simulationHz: 20, targetValidationHz: 5,
    targetSearchHz: 2.5, behaviorHz: 2.5 });
/** Type and allegiance are independent domains: do not compare them to each other. */
export const COMBAT_ACTOR_TYPE = Object.freeze({ HOUND: 0, BASTION: 1, TECHNICAL: 2 });
export const COMBAT_FACTION = Object.freeze({ FERAL: 0, WARDEN: 1, ROVERS: 2 });
export const COMBAT_DAMAGE_ZONE = Object.freeze({ NONE: 0, FRONT: 1, LEFT: 2, RIGHT: 3, REAR: 4 });
export const combatDefaultFaction = (type) => type === COMBAT_ACTOR_TYPE.HOUND ? COMBAT_FACTION.FERAL : type === COMBAT_ACTOR_TYPE.BASTION ? COMBAT_FACTION.WARDEN : COMBAT_FACTION.ROVERS;
export const COMBAT_EVENT_KINDS = ["hound_gun", "gun", "cannon", "quad_burst", "rocket_launch", "impact", "explosion", "disabled", "reinforcement"];
export const COMBAT_VEHICLE_TEST_SCENARIOS = ["healthy", "front", "left", "right", "rear", "heavy", "crash"];
const uint = (n) => Number.isSafeInteger(n) && n >= 0;
const finite = (n) => typeof n === "number" && Number.isFinite(n);
const actorId = (n) => uint(n) && n >= 1 && n <= COMBAT_LIMITS.actors;
const point = (p) => Array.isArray(p) && p.length === 3 && p.every(finite) && p.every((n) => Math.abs(n) <= WORLD_MAX_COORDINATE);
const record = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const keys = (v, allowed) => Object.keys(v).every((k) => allowed.includes(k));
export function isPackedCombatActor(v) {
    return Array.isArray(v) && v.length === 20 && v.every(finite) && actorId(v[0]) && uint(v[1])
        && isCombatActorType(v[2]) && isCombatFaction(v[3]) && point(v.slice(4, 7))
        && Math.abs(v[7]) <= Math.PI + 0.001 && Math.abs(v[8]) <= Math.PI + 0.001 && Math.abs(v[9]) <= Math.PI / 2
        && Math.abs(v[10]) <= 20 && Math.abs(v[11]) <= 20 && v[12] >= 0 && v[12] <= 1
        && uint(v[13]) && v[13] <= 5 && uint(v[14])
        && Math.abs(v[15]) <= 1 && v[16] >= -5 && v[16] <= 14
        && uint(v[17]) && v[17] <= 3 && uint(v[18]) && v[18] <= 4 && uint(v[19]) && v[19] <= 0xffffffff;
}
export function isPackedCombatRocket(v) {
    return Array.isArray(v) && v.length === 11 && v.every(finite) && uint(v[0]) && uint(v[1]) && actorId(v[2])
        && isCombatFaction(v[3]) && point(v.slice(4, 7)) && v.slice(7, 10).every((n) => Math.abs(n) <= 60) && uint(v[10]);
}
export function isCombatEvent(v) {
    return record(v) && keys(v, ["id", "epoch", "tick", "kind", "actorId", "actorEpoch", "weapon", "from", "to", "targetId", "targetEpoch", "projectileId", "seed", "durationMs", "rounds"])
        && uint(v.id) && uint(v.epoch) && uint(v.tick) && COMBAT_EVENT_KINDS.includes(v.kind)
        && actorId(v.actorId) && uint(v.actorEpoch) && point(v.from) && point(v.to)
        && (v.weapon === undefined || ["hound_gun", "gun", "cannon", "quad", "rocket"].includes(v.weapon))
        && (v.targetId === undefined || actorId(v.targetId)) && (v.targetEpoch === undefined || uint(v.targetEpoch))
        && (v.projectileId === undefined || uint(v.projectileId))
        && ((v.targetId === undefined) === (v.targetEpoch === undefined))
        && (v.seed === undefined || (uint(v.seed) && v.seed <= 0xffffffff))
        && (v.kind === "quad_burst"
            ? v.weapon === "quad" && uint(v.rounds) && v.rounds >= 4 && v.rounds <= COMBAT_LIMITS.quadRounds
                && uint(v.durationMs) && v.durationMs >= 100 && v.durationMs <= COMBAT_LIMITS.quadDurationMs && uint(v.seed)
            : v.durationMs === undefined && v.rounds === undefined)
        && (v.kind !== "hound_gun" || v.weapon === "hound_gun")
        && (v.kind !== "cannon" || v.weapon === "cannon");
}
export function isCombatEventList(v) {
    return Array.isArray(v) && v.length <= COMBAT_LIMITS.eventsPerPacket && v.every(isCombatEvent)
        && new Set(v.map((e) => e.id)).size === v.length;
}
export function isCombatSnapshot(v) {
    return record(v) && uint(v.epoch) && uint(v.tick) && typeof v.enabled === "boolean"
        && ["SKIRMISH", "ENGAGEMENT", "HEAVY_EXCHANGE", "RECOVERY", "REINFORCEMENT"].includes(v.phase)
        && Array.isArray(v.actors) && v.actors.length <= COMBAT_LIMITS.actors && v.actors.every(isPackedCombatActor)
        && new Set(v.actors.map((a) => a[0])).size === v.actors.length
        && v.actors.filter((a) => a[2] === COMBAT_ACTOR_TYPE.TECHNICAL).length <= COMBAT_LIMITS.maxTechnicals
        && Array.isArray(v.rockets) && v.rockets.length <= COMBAT_LIMITS.rockets && v.rockets.every(isPackedCombatRocket)
        && new Set(v.rockets.map((r) => r[0])).size === v.rockets.length;
}
export function isClientCombatDeveloperCommand(v) {
    if (!record(v) || v.t !== "dev_cmd")
        return false;
    if (v.command === "battle_reset")
        return keys(v, ["t", "command"]);
    if (v.command === "battle_pause")
        return keys(v, ["t", "command", "enabled"]) && typeof v.enabled === "boolean";
    if (v.command === "battle_vehicle_test")
        return keys(v, ["t", "command", "scenario"]) && COMBAT_VEHICLE_TEST_SCENARIOS.includes(v.scenario);
    if (v.command === "battle_spawn")
        return keys(v, ["t", "command", "actorType"]) && isCombatActorType(v.actorType);
    return v.command === "battle_preset" && keys(v, ["t", "command", "hounds", "bastions", "technicals"])
        && uint(v.hounds) && uint(v.bastions) && (v.technicals === undefined || (uint(v.technicals) && v.technicals <= COMBAT_LIMITS.maxTechnicals))
        && v.hounds + v.bastions + (v.technicals ?? 0) >= 1 && v.hounds + v.bastions + (v.technicals ?? 0) <= COMBAT_LIMITS.actors;
}
/** Naming alias for the central developer-message dispatcher. */
export const isCombatDeveloperCommand = isClientCombatDeveloperCommand;
export function isServerCombatSnapshot(v) {
    return record(v) && keys(v, ["t", "at", "epoch", "tick", "enabled", "phase", "actors", "rockets"])
        && v.t === "combat" && finite(v.at) && v.at >= 0 && isCombatSnapshot(v)
        && v.rockets.every((r) => r[1] === v.epoch && r[10] <= v.tick);
}
export function isServerCombatEvents(v) {
    return record(v) && keys(v, ["t", "at", "epoch", "tick", "events"])
        && v.t === "combat_events" && finite(v.at) && v.at >= 0 && uint(v.epoch) && uint(v.tick)
        && isCombatEventList(v.events) && v.events.every((e) => e.epoch === v.epoch && e.tick <= v.tick);
}
export const isCombatActorType = (v) => v === 0 || v === 1 || v === 2;
export const isCombatFaction = (v) => v === 0 || v === 1 || v === 2;
/** Canonical visual/hit/culling dimensions; Y roots are always the terrain surface. */
const COMBAT_GEOMETRY = [
    { length: 1.9, width: 1.1, height: 1.55, centerY: .85, hitRadius: .85, cullRadius: 2.1, shadowRadius: 1.2 },
    { length: 7.6, width: 3.95, height: 3.2, centerY: 1.55, hitRadius: 4.3, cullRadius: 5.4, shadowRadius: 4.2 },
    { length: 5.4, width: 2.05, height: 3.0, cabHeight: 2.0, centerY: 1.35, hitRadius: 3.0, cullRadius: 4.3, shadowRadius: 2.9 },
];
export const combatActorGeometry = (type) => COMBAT_GEOMETRY[type];
export function combatWeaponOrigin(a, weapon, barrelIndex = 0) {
    let x = a.x, y = a.y, z = a.z, side = 0, up = 0, forward = 0;
    if (weapon === "hound_gun") {
        x += Math.sin(a.yaw) * .64;
        z += Math.cos(a.yaw) * .64;
        y += 1.08;
        side = .22;
        forward = .56;
    }
    else if (weapon === "quad") {
        x -= Math.sin(a.yaw) * 1.25;
        z -= Math.cos(a.yaw) * 1.25;
        y += 2.05;
        side = barrelIndex % 2 ? .23 : -.23;
        up = barrelIndex % 4 >= 2 ? .46 : .12;
        forward = 1.30;
    }
    else if (weapon === "cannon") {
        x += Math.sin(a.turretYaw) * 1.25;
        z += Math.cos(a.turretYaw) * 1.25;
        y += 2.16;
        forward = 3.25;
    }
    else {
        y += 2.63;
        side = weapon === "gun" ? .95 : -1.05;
        forward = weapon === "gun" ? 2.25 : 1.8;
    }
    const pitchedForward = forward * Math.cos(a.aimPitch) - up * Math.sin(a.aimPitch);
    return [x + Math.sin(a.turretYaw) * pitchedForward + Math.cos(a.turretYaw) * side,
        y + forward * Math.sin(a.aimPitch) + up * Math.cos(a.aimPitch),
        z + Math.cos(a.turretYaw) * pitchedForward - Math.sin(a.turretYaw) * side];
}
export const VEHICLE_OCCUPANT_KIND = Object.freeze({ AI: "AI", PLAYER: "PLAYER" });
export function getCombatVehicleSeats(actorId, lifeEpoch) {
    return [
        { id: "driver", role: "DRIVER", mount: { parent: "hull", position: [-.45, 1.03, .60] }, occupant: { kind: "AI", id: `crew:${actorId}:${lifeEpoch}:driver` } },
        { id: "gunner", role: "GUNNER", mount: { parent: "turret", position: [0, -.52, -.70] }, occupant: { kind: "AI", id: `crew:${actorId}:${lifeEpoch}:gunner` } },
    ];
}
export const COMBAT_VEHICLE_CAMERA_ANCHOR = [0, 2.6, -.3];
